import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useUser } from "@/contexts/UserContext";
import { useLanguage } from "@/contexts/LanguageContext";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Shield, FileText, Download, Eye, CheckCircle2, ChevronLeft, ChevronRight } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { useState } from "react";
import { Document, Page, pdfjs } from 'react-pdf';

// Use jsdelivr CDN with proper CORS headers
pdfjs.GlobalWorkerOptions.workerSrc = `https://cdn.jsdelivr.net/npm/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

export const EthicsComplianceTab = () => {
  const { user } = useUser();
  const { t } = useLanguage();
  const { toast } = useToast();
  const [viewingPolicy, setViewingPolicy] = useState<any>(null);
  const [policyUrl, setPolicyUrl] = useState<string | null>(null);
  const [numPages, setNumPages] = useState<number | null>(null);
  const [pageNumber, setPageNumber] = useState<number>(1);

  // Fetch user profile
  const { data: profile } = useQuery({
    queryKey: ["profile", user?.id],
    queryFn: async () => {
      if (!user?.id) return null;
      // @ts-ignore - admin_bubble_id exists but not in types yet
      const { data } = await supabase
        .from("profiles")
        .select("admin_bubble_id, id")
        .eq("id", user.id)
        .single();
      return data;
    },
    enabled: !!user?.id,
  });

  // @ts-ignore - admin_bubble_id not in types yet
  const { data: policies = [], isLoading } = useQuery({
    // @ts-ignore
    queryKey: ["company-policies", profile?.admin_bubble_id],
    queryFn: async () => {
      // @ts-ignore
      if (!profile?.admin_bubble_id) return [];

      console.log("🔍 Fetching policies for admin_bubble_id:", profile.admin_bubble_id);

      // @ts-ignore - New table not in types yet
      const { data, error } = await supabase
        // @ts-ignore
        .from("company_policies")
        .select("*")
        // @ts-ignore
        .eq("admin_bubble_id", profile.admin_bubble_id)
        .eq("is_active", true)
        .order("policy_type");

      if (error) {
        console.error("❌ Error fetching policies:", error);
        throw error;
      }

      console.log("✅ Policies data:", data);
      return data || [];
    },
    // @ts-ignore
    enabled: !!profile?.admin_bubble_id,
  });

  // @ts-ignore - policy_acknowledgements not in types yet
  const { data: acknowledgements = [] } = useQuery({
    // @ts-ignore
    queryKey: ["my-policy-acknowledgements", user?.id],
    queryFn: async () => {
      // @ts-ignore
      if (!user?.id) return [];

      console.log("🔍 Fetching acknowledgements for user:", user.id);

      // @ts-ignore - New table not in types yet
      const { data, error } = await supabase
        // @ts-ignore
        .from("policy_acknowledgements")
        .select("*")
        // @ts-ignore
        .eq("user_id", user.id);

      if (error) {
        console.error("❌ Error fetching acknowledgements:", error);
        throw error;
      }
      
      console.log("✅ Acknowledgements data:", data);
      return data || [];
    },
    // @ts-ignore
    enabled: !!user?.id,
  });

  const getPolicyLabel = (policyType: string) => {
    switch (policyType) {
      case "code_of_ethics":
        return t("codeOfEthics");
      case "code_of_conduct":
        return t("codeOfConduct");
      case "gdpr_policy":
        return t("gdprPolicy");
      default:
        return policyType;
    }
  };

  const handleView = async (policy: any) => {
    try {
      const { data, error } = await supabase.storage
        .from("company-policies")
        .download(policy.file_url);

      if (error) throw error;
      
      const url = URL.createObjectURL(data);
      setPolicyUrl(url);
      setViewingPolicy(policy);
    } catch (error: any) {
      toast({
        title: t("error"),
        description: error.message,
        variant: "destructive",
      });
    }
  };

  const handleDownload = async (policy: any) => {
    try {
      const { data, error } = await supabase.storage
        .from("company-policies")
        .download(policy.file_url);

      if (error) throw error;

      const url = window.URL.createObjectURL(data);
      const a = document.createElement("a");
      a.href = url;
      a.download = policy.file_name;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error: any) {
      toast({
        title: t("error"),
        description: error.message,
        variant: "destructive",
      });
    }
  };

  const isAcknowledged = (policyId: string) => {
    const result = acknowledgements.some((ack: any) => ack.policy_id === policyId);
    console.log(`🔍 Checking if policy ${policyId} is acknowledged:`, result, "Total acknowledgements:", acknowledgements.length);
    return result;
  };

  const onDocumentLoadSuccess = ({ numPages }: { numPages: number }) => {
    setNumPages(numPages);
    setPageNumber(1);
  };

  if (isLoading) {
    return <div>Loading...</div>;
  }

  if (policies.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-center">
        <Shield className="h-16 w-16 text-muted-foreground mb-4" />
        <h3 className="text-lg font-semibold mb-2">{t("noActivePolicy")}</h3>
        <p className="text-muted-foreground">
          Company policies will appear here once uploaded by administrators
        </p>
      </div>
    );
  }

  return (
    <>
      <Dialog open={!!viewingPolicy} onOpenChange={(open) => {
        if (!open) {
          if (policyUrl) {
            URL.revokeObjectURL(policyUrl);
            setPolicyUrl(null);
          }
          setViewingPolicy(null);
          setNumPages(null);
          setPageNumber(1);
        }
      }}>
        <DialogContent className="max-w-full sm:max-w-4xl h-[95vh] sm:h-[90vh] flex flex-col p-3 sm:p-6">
          <DialogHeader className="pb-3">
            <DialogTitle className="text-base sm:text-lg">{viewingPolicy && getPolicyLabel(viewingPolicy.policy_type)}</DialogTitle>
          </DialogHeader>
          <div className="flex-1 overflow-auto min-h-0 flex flex-col items-center bg-muted/30 rounded-lg p-2">
            {policyUrl && (
              <>
                <div className="w-full flex justify-center">
                  <Document
                    file={policyUrl}
                    onLoadSuccess={onDocumentLoadSuccess}
                    loading={<div className="text-center py-8 text-sm">Loading PDF...</div>}
                    error={<div className="text-center py-8 text-destructive text-sm">Error loading PDF</div>}
                  >
                    <Page 
                      pageNumber={pageNumber}
                      renderTextLayer={false}
                      renderAnnotationLayer={false}
                      width={window.innerWidth < 640 ? window.innerWidth - 40 : undefined}
                      className="max-w-full shadow-lg"
                    />
                  </Document>
                </div>
                {numPages && numPages > 1 && (
                  <div className="mt-4 flex items-center gap-2 sm:gap-4 pb-4 sticky bottom-0 bg-background/95 backdrop-blur-sm p-3 rounded-lg border shadow-lg">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPageNumber(prev => Math.max(prev - 1, 1))}
                      disabled={pageNumber <= 1}
                      className="touch-manipulation"
                    >
                      <ChevronLeft className="h-4 w-4 sm:mr-1" />
                      <span className="hidden sm:inline">Previous</span>
                    </Button>
                    <span className="text-xs sm:text-sm font-medium whitespace-nowrap px-2">
                      {pageNumber} / {numPages}
                    </span>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPageNumber(prev => Math.min(prev + 1, numPages))}
                      disabled={pageNumber >= numPages}
                      className="touch-manipulation"
                    >
                      <span className="hidden sm:inline">Next</span>
                      <ChevronRight className="h-4 w-4 sm:ml-1" />
                    </Button>
                  </div>
                )}
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>

      <div className="space-y-6">
        <div className="flex items-center gap-3">
          <Shield className="h-6 w-6 text-primary" />
          <div>
            <h2 className="text-2xl font-bold">{t("ethicsCompliance")}</h2>
            <p className="text-muted-foreground">{t("companyPolicies")}</p>
          </div>
        </div>

      <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
        {policies.map((policy: any) => (
          <Card key={policy.id} className="relative overflow-hidden">
            {isAcknowledged(policy.id) && (
              <div className="absolute inset-0 pointer-events-none flex items-center justify-center z-10">
                <div className="flex items-center gap-2 bg-green-500/10 dark:bg-green-500/20 border-2 border-green-500/30 text-green-700 dark:text-green-400 font-bold text-lg px-6 py-3 rounded-lg rotate-[-12deg] shadow-lg backdrop-blur-sm">
                  <CheckCircle2 className="h-6 w-6" />
                  {t("acknowledged")}
                </div>
              </div>
            )}
            <CardHeader>
              <div className="flex items-center justify-between mb-2">
                <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center">
                  <FileText className="h-5 w-5 text-primary" />
                </div>
                {isAcknowledged(policy.id) && (
                  <div className="flex items-center gap-1 text-green-600 text-xs font-semibold">
                    <CheckCircle2 className="h-4 w-4" />
                    {t("acknowledged")}
                  </div>
                )}
              </div>
              <CardTitle>{getPolicyLabel(policy.policy_type)}</CardTitle>
              <CardDescription>
                {t("currentVersion")}: {policy.version}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-2">
              <p className="text-sm text-muted-foreground mb-4">{policy.file_name}</p>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  className="flex-1"
                  onClick={() => handleView(policy)}
                >
                  <Eye className="h-4 w-4 mr-2" />
                  {t("viewPolicy")}
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  className="flex-1"
                  onClick={() => handleDownload(policy)}
                >
                  <Download className="h-4 w-4 mr-2" />
                  {t("downloadPolicy")}
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}
        </div>
      </div>
    </>
  );
};
